import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Inbox, Settings, Moon, Sun, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { assetUrl } from "@/lib/api";
import { Paywall } from "./Paywall";
import { EmailBanner } from "./EmailBanner";
import { ProfileMenu } from "./ProfileMenu";

const THEME_KEY = "sf-theme";

const useTheme = () => {
  const [dark, setDark] = useState(() => localStorage.getItem(THEME_KEY) === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem(THEME_KEY, dark ? "dark" : "light");
    return () => document.documentElement.classList.remove("dark");
  }, [dark]);
  return [dark, () => setDark((d) => !d)];
};

const Logo = ({ user, size = "h-9 w-9" }) =>
  user.logo_url ? (
    <img src={assetUrl(user.logo_url)} alt="" className={`${size} rounded-lg border bg-white object-cover`} data-testid="dashboard-logo" />
  ) : (
    <span className={`flex ${size} items-center justify-center rounded-lg bg-[color:var(--brand)] text-sm font-bold text-white`} data-testid="dashboard-logo">
      {user.company_name.split(" ").slice(0, 2).map((w) => w[0]).join("")}
    </span>
  );

const NAV = [
  { to: "/dashboard", icon: Inbox, label: "Leads", testId: "nav-leads-link" },
  { to: "/settings", icon: Settings, label: "Settings", testId: "nav-settings-link" },
];
const ADMIN_NAV = { to: "/admin", icon: ShieldCheck, label: "Admin", testId: "nav-admin-link" };
const navFor = (user) => (user.is_admin ? [...NAV, ADMIN_NAV] : NAV);

const ThemeButton = ({ dark, toggle, className = "", testId = "theme-toggle-button" }) => (
  <button type="button" onClick={toggle} aria-label="Toggle dark mode" data-testid={testId}
    className={`flex h-10 items-center gap-2 rounded-lg px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground ${className}`}>
    {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}<span className="hidden lg:inline">{dark ? "Light mode" : "Dark mode"}</span>
  </button>
);

const Sidebar = ({ user, dark, toggle }) => (
  <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r bg-card lg:flex" data-testid="dashboard-sidebar">
    <div className="flex h-16 items-center border-b px-3">
      <ProfileMenu testId="profile-menu" trigger={<>
        <Logo user={user} />
        <p className="truncate text-sm font-semibold text-foreground" data-testid="dashboard-company-name">{user.company_name}</p>
      </>} />
    </div>
    <nav className="flex-1 space-y-1 p-3">
      {navFor(user).map(({ to, icon: Icon, label, testId }) => (
        <NavLink key={to} to={to} data-testid={testId}
          className={({ isActive }) => `flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors ${
            isActive ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"}`}>
          <Icon className="h-4 w-4" />{label}
        </NavLink>
      ))}
    </nav>
    <div className="border-t p-3"><ThemeButton dark={dark} toggle={toggle} className="w-full" testId="sidebar-theme-toggle-button" /></div>
  </aside>
);

const MobileTop = ({ user, dark, toggle }) => (
  <header className="sticky top-0 z-30 border-b bg-card/85 backdrop-blur-xl lg:hidden" data-testid="dashboard-header">
    <div className="flex h-14 items-center justify-between gap-3 px-4">
      <ProfileMenu testId="mobile-profile-menu" trigger={<>
        <Logo user={user} size="h-8 w-8" />
        <p className="truncate text-sm font-semibold text-foreground">{user.company_name}</p>
      </>} />
      <ThemeButton dark={dark} toggle={toggle} />
    </div>
  </header>
);

const BottomNav = ({ user }) => (
  <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t bg-card/95 backdrop-blur-xl lg:hidden"
    style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-bottom-nav">
    {navFor(user).map(({ to, icon: Icon, label, testId }) => (
      <NavLink key={to} to={to} data-testid={`mobile-${testId}`}
        className={({ isActive }) => `flex h-16 flex-1 flex-col items-center justify-center gap-1 text-xs font-medium ${isActive ? "text-[color:var(--brand)]" : "text-muted-foreground"}`}>
        <Icon className="h-5 w-5" />{label}
      </NavLink>
    ))}
  </nav>
);

export const AppShell = ({ children, hideNav = false, allowInactive = false }) => {
  const { user } = useAuth();
  const [dark, toggle] = useTheme();
  if (user.email_verified && !user.subscription.is_active && !(allowInactive && user.is_admin)) return <Paywall />;
  return (
    <div className="min-h-screen bg-background text-foreground" style={{ "--brand": user.brand_color }}>
      <Sidebar user={user} dark={dark} toggle={toggle} />
      <MobileTop user={user} dark={dark} toggle={toggle} />
      <main className="pb-28 lg:pb-12 lg:pl-64">
        <div className="mx-auto max-w-5xl px-4 pt-5 sm:px-6 lg:pt-8">{!user.email_verified && <EmailBanner />}{children}</div>
      </main>
      {!hideNav && <BottomNav user={user} />}
    </div>
  );
};
